import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CTASectionProps {
  content: {
    title?: string;
    subtitle?: string;
    buttonText?: string;
    buttonLink?: string;
    highlights?: { text: string }[];
    backgroundImage?: string;
  };
  variant?: string;
  layoutStyle?: string;
}

export function CTASection({ content, variant = 'default', layoutStyle = 'classic' }: CTASectionProps) {
  const {
    title = 'Ready to get started?',
    subtitle,
    buttonText = 'Contact Us',
    buttonLink = '/booking',
    highlights = [],
    backgroundImage,
  } = content;

  const isMinimal = layoutStyle === 'modern-minimal';
  const isBold = layoutStyle === 'bold-geometric';
  const isPlayful = layoutStyle === 'playful-rounded';

  if (variant === 'simple') {
    return (
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              {title}
            </h2>
            {subtitle && (
              <p className="text-lg text-muted-foreground">{subtitle}</p>
            )}
            <Button variant="hero" size="xl" className="group" asChild>
              <Link to={buttonLink}>
                {buttonText}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className={cn(
        "section-padding",
        isBold ? "bg-foreground text-background" : "bg-primary"
      )}
      style={backgroundImage ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : undefined}
    >
      {backgroundImage && <div className="absolute inset-0 bg-primary/80" />}
      <div className="container mx-auto px-4 relative z-10">
        <div className={cn(
          "max-w-3xl mx-auto text-center",
          isBold ? "text-background" : "text-primary-foreground"
        )}>
          <h2 className={cn(
            "font-display font-bold mb-4",
            isMinimal ? "text-3xl md:text-4xl tracking-tight" :
            isBold ? "text-3xl md:text-5xl uppercase tracking-wide" :
            "text-3xl md:text-4xl"
          )}>
            {title}
          </h2>
          {subtitle && (
            <p className={cn(
              "text-lg mb-8 max-w-xl mx-auto",
              isBold ? "text-background/80" : "text-primary-foreground/80"
            )}>
              {subtitle}
            </p>
          )}

          <Button 
            size="xl" 
            variant="secondary" 
            className={cn(
              "group",
              isBold ? "bg-background text-foreground hover:bg-background/90 rounded-none uppercase tracking-wider" :
              isPlayful ? "bg-card text-foreground hover:bg-card/90 rounded-full" :
              "bg-card text-foreground hover:bg-card/90"
            )}
            asChild
          >
            <Link to={buttonLink}>
              {buttonText}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>

          {highlights.length > 0 && (
            <div className={cn(
              "flex flex-wrap items-center justify-center gap-6 mt-8 text-sm",
              isBold ? "text-background/80" : "text-primary-foreground/80"
            )}>
              {highlights.map((h, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {h.text}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}